import { Component, type ReactNode } from "react";
import { reportClientError } from "./customer-activity";
import "./application-error-boundary.css";

type Props = {
  children: ReactNode;
  reload?: () => void;
};

type State = {
  failed: boolean;
  /** The page's code came from an earlier release: reloading gets the current one. */
  staleBuild: boolean;
};

const STALE_BUILD_RELOAD_KEY = "lc:stale-build-reload-at";

/**
 * A deploy replaces the site's files; a tab opened before it can then fail to load the next
 * screen (end-to-end run 8, 2026-10-15: right after an order was sent).
 */
export function isStaleBuildError(error: unknown) {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/iu.test(
    text,
  );
}

/** This page's own mark, so a reload it asked for is told apart from the page after it. */
const pageMark = Math.random().toString(36).slice(2);

function lastReload() {
  const [at, page] = (window.sessionStorage.getItem(STALE_BUILD_RELOAD_KEY) ?? "").split(":");
  return { at: Number(at), page };
}

/** Reloads to the current release at most once a minute, so a missing file can never loop. */
export function reloadForNewBuild(
  reload: () => void = () => window.location.reload(),
  now = Date.now(),
) {
  try {
    const last = lastReload().at;
    if (Number.isFinite(last) && now - last < 60_000) return false;
    window.sessionStorage.setItem(STALE_BUILD_RELOAD_KEY, `${now}:${pageMark}`);
  } catch {
    return false;
  }
  reload();
  return true;
}

/** Whether this very page already asked for the reload, which is still on its way. */
function reloadUnderway() {
  try {
    return lastReload().page === pageMark;
  } catch {
    return false;
  }
}

export class ApplicationErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false, staleBuild: false };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, staleBuild: isStaleBuildError(error) };
  }

  override componentDidCatch(error: unknown) {
    // Keep the browser record content-free. Provider diagnostics can key off this stable code.
    console.error("APPLICATION_RENDER_FAILED");
    reportClientError(isStaleBuildError(error) ? "CHUNK_LOAD_FAILED" : "RENDER_FAILED");
    if (isStaleBuildError(error) && !reloadForNewBuild(this.reload) && !reloadUnderway())
      this.setState({ staleBuild: false });
  }

  private reload = () => {
    if (this.props.reload) {
      this.props.reload();
      return;
    }
    window.location.reload();
  };

  override render() {
    if (!this.state.failed) return this.props.children;

    if (this.state.staleBuild)
      return (
        <main id="main-content" className="application-error-boundary">
          <section role="status" aria-labelledby="application-error-title">
            <p className="kicker kicker--dark">We’ve just updated the site</p>
            <h1 id="application-error-title">Loading the latest version…</h1>
            <p>Anything you’ve already saved is kept.</p>
            <div className="button-row">
              <button className="button button--dark" type="button" onClick={this.reload}>
                Reload this page
              </button>
            </div>
          </section>
        </main>
      );

    // Honest about what may have happened: the failure can follow a request that went through,
    // such as a print order (end-to-end run 8, 2026-10-15).
    return (
      <main id="main-content" className="application-error-boundary">
        <section role="alert" aria-labelledby="application-error-title">
          <p className="kicker kicker--dark">This page didn’t finish loading</p>
          <h1 id="application-error-title">Let’s try that again.</h1>
          <p>
            Reloading usually fixes this, and anything you’ve already saved is kept. If you had just
            sent something, like a print order, it may already have gone through: reload to see
            where it is.
          </p>
          <div className="button-row">
            <button className="button button--dark" type="button" onClick={this.reload}>
              Reload this page
            </button>
            <a className="button button--outline" href="/">
              Return home
            </a>
          </div>
          <small>
            If this keeps happening, email lifescapsule@jpwlabs.com with the page address.
          </small>
        </section>
      </main>
    );
  }
}
